import {useEffect,useMemo,useState} from 'react';
import type {IntentProfileRecord,TaskTemplateStep} from '../lib/catalog';

type Decision='approved'|'modified'|'denied'|null;

type Props={
  profile:IntentProfileRecord|null;
  onEvent:(text:string,tone?:'ok'|'warn'|'info')=>void;
};

function stepState(
  index:number,
  phase:number,
  step:TaskTemplateStep,
  decision:Decision,
){
  if(step.type==='approval'&&decision==='denied')return 'denied';
  if(index<phase)return 'complete';
  if(index===phase){
    if(step.type==='approval'&&decision==='approved')return 'complete';
    if(step.type==='approval'&&decision==='modified')return 'modified';
    return 'active';
  }
  return 'pending';
}

export default function TaskGraph({profile,onEvent}:Props){
  const [phase,setPhase]=useState(0);
  const [decision,setDecision]=useState<Decision>(null);

  const steps=profile?.task_template??[];
  const approvalIndex=useMemo(
    ()=>steps.findIndex(step=>step.type==='approval'),
    [steps]
  );

  useEffect(()=>{
    setPhase(0);
    setDecision(null);
    if(!profile||!steps.length)return;

    onEvent(`TASK GRAPH // ${steps.length} STEPS GENERATED`,'info');

    const stopIndex=approvalIndex>=0?approvalIndex:steps.length-1;
    const timers:number[]=[];

    for(let index=1;index<=stopIndex;index++){
      timers.push(window.setTimeout(()=>{
        setPhase(index);
        const step=steps[index];
        if(step){
          onEvent(`TASK STEP // ${step.label}`,step.type==='approval'?'warn':'info');
        }
      },index*850));
    }

    return()=>timers.forEach(window.clearTimeout);
  },[profile?.id]);

  if(!profile){
    return (
      <aside className="task-panel glass-panel">
        <div className="panel-label">TASK GRAPH</div>
        <div className="task-empty">
          <span>NO ACTIVE VECTOR</span>
          <strong>Select an intent to generate an orchestration graph.</strong>
          <p>The graph is driven by the intent profile stored in Supabase.</p>
        </div>
      </aside>
    );
  }

  const decide=(next:Exclude<Decision,null>)=>{
    setDecision(next);
    if(next==='denied'){
      onEvent('HUMAN GATE // ACTION DENIED','warn');
      return;
    }

    onEvent(
      next==='approved'
        ? 'HUMAN GATE // APPROVED'
        : 'HUMAN GATE // MODIFIED + APPROVED',
      'ok'
    );

    if(approvalIndex<0)return;

    steps.slice(approvalIndex+1).forEach((step,offset)=>{
      window.setTimeout(()=>{
        setPhase(approvalIndex+1+offset);
        onEvent(`TASK STEP // ${step.label}`,'info');
      },(offset+1)*700);
    });
  };

  return (
    <aside className="task-panel glass-panel">
      <div className="panel-label">TASK GRAPH // {profile.label}</div>
      <div className="task-head">
        <div>
          <span>{profile.title}</span>
          <strong>{profile.description}</strong>
        </div>
        <b className={profile.approval_mode==='required'?'approval-required':''}>
          {profile.approval_mode.toUpperCase()} APPROVAL
        </b>
      </div>

      <div className="task-steps">
        {steps.map((step,index)=>{
          const state=stepState(index,phase,step,decision);
          return (
            <div className={`task-step ${state}`} key={step.key}>
              <span className="task-index">{String(index+1).padStart(2,'0')}</span>
              <span className="task-node-dot"/>
              <div>
                <small>{step.type.toUpperCase()}</small>
                <strong>{step.label}</strong>
              </div>
            </div>
          );
        })}
      </div>

      {approvalIndex>=0&&phase===approvalIndex&&decision===null&&(
        <div className="approval-gate">
          <div>
            <span>HUMAN APPROVAL GATE</span>
            <strong>
              {profile.approval_mode==='required'
                ? 'Execution is blocked until a human decides.'
                : 'Human review is available before continuation.'}
            </strong>
          </div>
          <div className="approval-actions">
            <button onClick={()=>decide('approved')}>APPROVE</button>
            <button onClick={()=>decide('modified')}>MODIFY</button>
            <button className="deny" onClick={()=>decide('denied')}>DENY</button>
          </div>
        </div>
      )}

      {decision==='denied'&&(
        <div className="approval-result denied-result">WORKFLOW HALTED BY HUMAN</div>
      )}
      {(decision==='approved'||decision==='modified')&&(
        <div className="approval-result approved-result">HUMAN CONTROL VERIFIED</div>
      )}
    </aside>
  );
}
